import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * BypassGo home-page cylinder.
 *
 * Design choices to avoid the issues seen in earlier iterations:
 *
 * 1.  The blue end-caps are placed *INSIDE* the body, so the body's
 *     rounded `borderRadius` + `overflow: hidden` clip them along the
 *     cylinder's silhouette. They naturally hug the curve.
 *
 * 2.  No live `rotateX` 3D transform on the text stage. Pure GPU
 *     `translate3d` + `opacity` scrolling — no subpixel jitter, no
 *     font-weight / font-size reflows during the transition.
 *
 * 3.  Each phrase slot uses an absolute index as React key so persisting
 *     slots smoothly transition their transform & opacity when the
 *     `counter` advances by one slot height.
 *
 * 4.  The cylinder body has a strong vertical lighting gradient
 *     (dark → bright → dark) and a soft drop shadow so the surface
 *     looks volumetric.
 *
 * 5.  3 s rest + 1.8 s slow scroll, fully automatic.
 */

const PHRASES = [
  "Gagnez du temps et limitez les attentes sur place grâce aux files à distance.",
  "Accédez à votre file d'attente à distance, simplement.",
  "Pilotez votre file d'attente en temps réel, simplement.",
];

const REST_MS = 3000;
const TRANSITION_MS = 1800;
const CYCLE_MS = REST_MS + TRANSITION_MS;
const SLOT_HEIGHT = 38;
const VIS_RANGE = 2; // 5 slots rendered (-2..+2)

const mod = (n, m) => ((n % m) + m) % m;

export default function CylinderPhrases() {
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCounter((c) => c + 1), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const active = mod(counter, PHRASES.length);

  return (
    <div
      className="mx-auto relative select-none"
      style={{ width: "min(100%, 380px)" }}
      data-testid="home-cylinder-phrases"
    >
      {/* Decorative chevrons */}
      <ChevronLeft
        size={22}
        strokeWidth={1.5}
        aria-hidden="true"
        className="absolute -left-1 top-1/2 -translate-y-1/2 text-[#9AA5BD] z-30 pointer-events-none"
      />
      <ChevronRight
        size={22}
        strokeWidth={1.5}
        aria-hidden="true"
        className="absolute -right-1 top-1/2 -translate-y-1/2 text-[#9AA5BD] z-30 pointer-events-none"
      />

      {/* Cylinder body — overflow:hidden lets the rounded shape clip the caps */}
      <div
        className="relative mx-5 overflow-hidden"
        style={{
          height: 130,
          borderRadius: "55% / 50%",
          background: `
            linear-gradient(180deg,
              #8FAAE0 0%,
              #B0C5EE 5%,
              #CDD8F5 11%,
              #E4EBFA 19%,
              #F2F6FD 30%,
              #FBFCFE 42%,
              #FFFFFF 50%,
              #FBFCFE 58%,
              #F2F6FD 70%,
              #E4EBFA 81%,
              #CDD8F5 89%,
              #B0C5EE 95%,
              #8FAAE0 100%
            )
          `,
          boxShadow: `
            0 14px 30px rgba(0,31,140,0.20),
            0 4px 10px rgba(0,31,140,0.10),
            inset 0 1px 0 rgba(255,255,255,0.95),
            inset 0 -2px 4px rgba(0,47,167,0.08)
          `,
        }}
      >
        {/* Left end-cap — solid blue band clipped by body's rounded shape */}
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0"
          style={{
            width: 46,
            background: `
              linear-gradient(to right,
                #001075 0%,
                #002FA7 18%,
                #1A4ECE 42%,
                #3568E6 58%,
                #1A4ECE 78%,
                #002FA7 100%
              )
            `,
            boxShadow: `
              inset -3px 0 6px rgba(0,15,80,0.45),
              inset 2px 0 0 rgba(255,255,255,0.18)
            `,
            zIndex: 1,
          }}
        />

        {/* Right end-cap */}
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 bottom-0"
          style={{
            width: 46,
            background: `
              linear-gradient(to left,
                #001075 0%,
                #002FA7 18%,
                #1A4ECE 42%,
                #3568E6 58%,
                #1A4ECE 78%,
                #002FA7 100%
              )
            `,
            boxShadow: `
              inset 3px 0 6px rgba(0,15,80,0.45),
              inset -2px 0 0 rgba(255,255,255,0.18)
            `,
            zIndex: 1,
          }}
        />

        {/* Top & bottom subtle curvature shadows (sit on the white area) */}
        <div
          aria-hidden="true"
          className="absolute left-12 right-12 top-0 h-3 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, rgba(0,47,167,0.18), rgba(0,47,167,0))",
            zIndex: 2,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute left-12 right-12 bottom-0 h-3 pointer-events-none"
          style={{
            background: "linear-gradient(to top, rgba(0,47,167,0.18), rgba(0,47,167,0))",
            zIndex: 2,
          }}
        />

        {/* Active panel — translucent white card framing the centre */}
        <div
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            left: 54,
            right: 54,
            top: "50%",
            transform: "translateY(-50%)",
            height: 50,
            borderRadius: 12,
            background: "rgba(255,255,255,0.85)",
            boxShadow:
              "0 2px 4px rgba(0,31,140,0.10), inset 0 0 0 1px rgba(0,47,167,0.08)",
            zIndex: 3,
          }}
        />

        {/* Scrolling phrase strip — pure 2D translateY, no jitter */}
        <div
          className="absolute inset-0"
          style={{
            paddingLeft: 56,
            paddingRight: 56,
            zIndex: 4,
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)",
          }}
        >
          <div className="relative w-full h-full" aria-live="polite">
            {Array.from({ length: VIS_RANGE * 2 + 1 }).map((_, idx) => {
              const rel = idx - VIS_RANGE;
              const k = counter + rel;
              const phraseIdx = mod(k, PHRASES.length);
              const isActive = rel === 0;
              const dist = Math.abs(rel);
              const opacity = dist === 0 ? 1 : dist === 1 ? 0.4 : 0.1;
              return (
                <p
                  key={k}
                  data-testid={isActive ? `home-cylinder-phrase-${phraseIdx}` : undefined}
                  className="absolute inset-x-0 text-center font-display tracking-tight leading-snug px-1"
                  style={{
                    top: "50%",
                    transform: `translate3d(0, calc(-50% + ${rel * SLOT_HEIGHT}px), 0)`,
                    transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.45, 0, 0.25, 1), opacity ${TRANSITION_MS}ms ease-in-out`,
                    opacity,
                    color: isActive ? "#002FA7" : "rgba(0,47,167,0.55)",
                    fontSize: isActive ? "16px" : "12px",
                    fontWeight: isActive ? 800 : 600,
                    filter: dist >= 1 ? "blur(0.3px)" : "none",
                    textShadow: isActive ? "0 1px 0 rgba(255,255,255,0.6)" : "none",
                    margin: 0,
                    willChange: "transform, opacity",
                  }}
                >
                  {PHRASES[phraseIdx]}
                </p>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div
        className="mt-4 flex items-center justify-center gap-2"
        role="tablist"
        aria-label="Phrase active"
        data-testid="home-cylinder-dots"
      >
        {PHRASES.map((_, i) => (
          <span
            key={i}
            role="tab"
            aria-selected={i === active}
            data-testid={`home-cylinder-dot-${i}`}
            className="block rounded-full transition-all duration-300"
            style={{
              width: i === active ? 14 : 8,
              height: 8,
              backgroundColor: i === active ? "#002FA7" : "#D6DBE5",
            }}
          />
        ))}
      </div>
    </div>
  );
}
